import { useCallback, useEffect, useState } from 'react';
import { Button, Drawer, Empty, List, Space, Tag, Typography, message } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  formatDateTime,
  supabase,
  useAuthUser,
  usePortalPermissions,
  type ReminderEvent,
} from '@ubus/shared';

const { Text } = Typography;

type InboxRow = ReminderEvent & {
  reminder_types?:
    | { name_en: string; name_zh: string }
    | { name_en: string; name_zh: string }[]
    | null;
  reminder_deliveries?: { channel: string; status: string }[] | null;
};

function eventTypeLabel(
  item: InboxRow,
  language: string
): string {
  const raw = item.reminder_types;
  const meta = Array.isArray(raw) ? raw[0] : raw;
  if (!meta) return item.type_code;
  return language === 'zh-CN' ? meta.name_zh : meta.name_en;
}

interface ReminderInboxDrawerProps {
  open: boolean;
  onClose: () => void;
  onUnreadChange?: (count: number) => void;
}

export async function fetchAdminReminderUnread(
  userId: string
): Promise<{ rows: InboxRow[]; unreadIds: string[] }> {
  const { data: deliveries, error } = await supabase
    .from('reminder_deliveries')
    .select('event_id, status')
    .eq('channel', 'in_app_admin')
    .in('status', ['sent', 'pending']);
  if (error) throw error;

  const eventIds = [...new Set((deliveries || []).map((d) => d.event_id))];
  if (eventIds.length === 0) return { rows: [], unreadIds: [] };

  const [{ data: events, error: evErr }, { data: reads, error: readErr }] = await Promise.all([
    supabase
      .from('reminder_events')
      .select('*, reminder_types(name_en, name_zh)')
      .in('id', eventIds)
      .eq('status', 'open')
      .order('created_at', { ascending: false })
      .limit(100),
    supabase
      .from('reminder_reads')
      .select('event_id')
      .eq('reader_kind', 'admin_user')
      .eq('reader_id', userId)
      .in('event_id', eventIds),
  ]);
  if (evErr) throw evErr;
  if (readErr) throw readErr;

  const readSet = new Set((reads || []).map((r) => r.event_id));
  const rows = (events || []) as InboxRow[];
  const unreadIds = rows.filter((r) => !readSet.has(r.id)).map((r) => r.id);
  return { rows, unreadIds };
}

function ReminderInboxDrawer({ open, onClose, onUnreadChange }: ReminderInboxDrawerProps) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const user = useAuthUser();
  const { access } = usePortalPermissions();
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<InboxRow[]>([]);
  const [unreadIds, setUnreadIds] = useState<string[]>([]);

  const readerLabel = () => {
    const name = access?.display_name?.trim();
    if (name) return name;
    const email = user?.email?.trim();
    if (email) return email.split('@')[0] || email;
    return user?.id?.slice(0, 8) || 'admin';
  };

  const reload = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      const { rows: next, unreadIds: ids } = await fetchAdminReminderUnread(user.id);
      setRows(next);
      setUnreadIds(ids);
      onUnreadChange?.(ids.length);
    } catch (err) {
      message.error(err instanceof Error ? err.message : t('reminders.inboxLoadFailed'));
    } finally {
      setLoading(false);
    }
  }, [user?.id, onUnreadChange, t]);

  useEffect(() => {
    if (open) void reload();
  }, [open, reload]);

  const markRead = async (eventId: string) => {
    if (!user?.id) return;
    const { error } = await supabase.from('reminder_reads').upsert(
      {
        event_id: eventId,
        reader_kind: 'admin_user',
        reader_id: user.id,
        reader_label: readerLabel(),
        read_at: new Date().toISOString(),
      },
      { onConflict: 'event_id,reader_kind,reader_id' }
    );
    if (error) {
      message.error(error.message);
      return;
    }
    const nextUnread = unreadIds.filter((id) => id !== eventId);
    setUnreadIds(nextUnread);
    onUnreadChange?.(nextUnread.length);
  };

  const markAllRead = async () => {
    if (!user?.id || unreadIds.length === 0) return;
    const label = readerLabel();
    const payload = unreadIds.map((eventId) => ({
      event_id: eventId,
      reader_kind: 'admin_user' as const,
      reader_id: user.id,
      reader_label: label,
      read_at: new Date().toISOString(),
    }));
    const { error } = await supabase
      .from('reminder_reads')
      .upsert(payload, { onConflict: 'event_id,reader_kind,reader_id' });
    if (error) {
      message.error(error.message);
      return;
    }
    setUnreadIds([]);
    onUnreadChange?.(0);
    message.success(t('reminders.markedAllRead'));
  };

  return (
    <Drawer
      title={t('reminders.inboxTitle')}
      open={open}
      onClose={onClose}
      width={420}
      extra={
        <Space>
          <Button size="small" disabled={!unreadIds.length} onClick={() => void markAllRead()}>
            {t('reminders.markAllRead')}
          </Button>
          <Button
            size="small"
            type="link"
            onClick={() => {
              onClose();
              navigate('/reminder-records');
            }}
          >
            {t('reminders.openRecords')}
          </Button>
        </Space>
      }
    >
      {rows.length === 0 && !loading ? (
        <Empty description={t('reminders.inboxEmpty')} />
      ) : (
        <List
          loading={loading}
          dataSource={rows}
          renderItem={(item) => {
            const unread = unreadIds.includes(item.id);
            return (
              <List.Item
                actions={[
                  unread ? (
                    <Button key="read" type="link" onClick={() => void markRead(item.id)}>
                      {t('reminders.markRead')}
                    </Button>
                  ) : (
                    <Tag key="done">{t('reminders.read')}</Tag>
                  ),
                ]}
              >
                <List.Item.Meta
                  title={
                    <Space>
                      {unread && <Tag color="processing">{t('reminders.unread')}</Tag>}
                      <Text>{eventTypeLabel(item, i18n.language)}</Text>
                    </Space>
                  }
                  description={
                    <div>
                      <div>
                        {item.subject_label || item.subject_id}
                        {typeof item.payload?.fleet_operator_name === 'string' &&
                        item.payload.fleet_operator_name
                          ? ` · ${item.payload.fleet_operator_name}`
                          : ''}{' '}
                        · {t('reminders.due')}: {item.due_date}
                        {item.ahead_days != null ? ` (−${item.ahead_days}d)` : ''}
                      </div>
                      <Text type="secondary">{formatDateTime(item.created_at)}</Text>
                    </div>
                  }
                />
              </List.Item>
            );
          }}
        />
      )}
    </Drawer>
  );
}

export default ReminderInboxDrawer;
